import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { useAuth } from "../../../hooks/useAuth";
import { getOrganizationsAsync } from "../../../api/OrganizationApi";
import { OrganizationResponse } from "../../../models/OrganizationModels";
import { Constants } from "../../../constants/constants";
import "./OrganizationSelectorPage.css";

const PAGE_SIZE = 12;

const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

const OrganizationSelectorPage = () => {
  const { user, selectPlatformOrganization } = useAuth();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<OrganizationResponse[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const loadOrganizations = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await getOrganizationsAsync(
        token,
        page,
        PAGE_SIZE,
        query || undefined,
      );
      if (res.success && res.result) {
        setItems(res.result.items);
        setTotalPages(res.result.totalPages || 1);
        setTotalCount(res.result.totalCount);
      } else {
        setItems([]);
        setError(res.message || "Failed to load organizations.");
      }
    } catch {
      setItems([]);
      setError("Failed to load organizations. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [token, page, query]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const handleSelect = (org: OrganizationResponse) => {
    selectPlatformOrganization(org);
    navigate("/dashboard");
  };

  const handleExitToPlatform = () => {
    selectPlatformOrganization(null);
    navigate("/dashboard");
  };

  if (!user?.isPlatformOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout
      activeSection="select-org"
      onSectionChange={(key) => navigate(`/dashboard?section=${key}`)}
    >
      <div className="org-select">
        <div className="org-select__header">
          <div>
            <h2 className="org-select__title">Select Organization</h2>
            <p className="org-select__subtitle">
              Pick an organization to manage. Search by name or slug.
            </p>
          </div>
          {user.activeOrganization && (
            <button
              className="org-select__exit"
              onClick={handleExitToPlatform}
            >
              Exit to Platform Admin
            </button>
          )}
        </div>

        {user.activeOrganization && (
          <div className="org-select__current">
            Currently managing{" "}
            <strong>{user.activeOrganization.name}</strong>
          </div>
        )}

        <div className="org-select__search">
          <input
            type="search"
            className="org-select__input"
            placeholder="Search organizations by name or slug…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search organizations"
          />
          {totalCount > 0 && (
            <span className="org-select__count">
              {totalCount} {totalCount === 1 ? "organization" : "organizations"}
            </span>
          )}
        </div>

        {error && <div className="org-select__error">{error}</div>}

        {loading ? (
          <div className="org-select__status">Loading organizations…</div>
        ) : items.length === 0 ? (
          <div className="org-select__status">
            {query
              ? `No organizations match "${query}".`
              : "No organizations yet."}
          </div>
        ) : (
          <>
            <div className="org-select__grid">
              {items.map((org) => (
                <button
                  key={org.id}
                  className="org-select__card"
                  onClick={() => handleSelect(org)}
                >
                  <span className="org-select__avatar">{initials(org.name)}</span>
                  <span className="org-select__body">
                    <span className="org-select__name">{org.name}</span>
                    <span className="org-select__meta">
                      /{org.slug}
                      {org.city ? ` • ${org.city}` : ""}
                    </span>
                  </span>
                  <span
                    className={`org-select__badge ${
                      org.isActive ? "active" : "inactive"
                    }`}
                  >
                    {org.isActive ? "Active" : "Inactive"}
                  </span>
                </button>
              ))}
            </div>

            <div className="org-select__pagination">
              <button
                className="org-select__page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </button>
              <span className="org-select__page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="org-select__page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrganizationSelectorPage;